import { CheckboxField, TableInput } from "../../../common/FormFields";
import { EditableTable } from "../../../common/Tables";
import AddRowBtn from "../../../common/AppSpecificConpnents/AddRowBtn";
import InputWithPen from "../../../common/Inputs/InputWithPen";
import RenderHtml, { InputDescription } from "../../../common/AppSpecificConpnents/PartsTable/RenderHtml";
import RenderUnits from "../../Canvas/components/canvas-core/RenderUnits";
import PartsPopup from "../../../common/AppSpecificConpnents/PartsTable/PartsPopup";
import { getUnitNameById } from "../../../util/AppUtils";
import { useState, useMemo, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getPartById } from "../../../redux/partSlice";
import { getAllSuggestionRules } from "../../../redux/suggestionSlice";

/**
 * BOMTable - Bill of Materials editable table component
 *
 * Handles its own add row logic and parts modal internally. The component will:
 * - Check for existing blank rows before adding new ones
 * - Automatically set editing index when adding rows
 * - Update parent state via onBomTableChange and onEditingIndexChange
 * - Manage parts modal state and selection internally
 * - Validate qty/unit relationship
 *
 * @param {Object} props
 * @param {Array} props.bomTable - Array of BOM table rows
 * @param {function} props.onBomTableChange - Callback when BOM table changes
 * @param {number} props.editingIndex - Index of currently editing row
 * @param {function} props.onEditingIndexChange - Callback when editing index changes
 * @param {boolean} props.isHideBomHeading - Whether to hide BOM table in document
 * @param {function} props.onHideBomHeadingChange - Callback for checkbox change
 * @param {Array} props.units - Available units from Redux
 * @param {function} props.onAddRow - Optional callback notification when row is added
 * @param {function} props.onUnsavedChanges - Callback to mark unsaved changes
 * @param {function} props.onValidationError - Callback when validation fails
 * @param {function} props.onShowWarning - Callback to show warning snackbar
 * @param {number} props.editingStepIndex - Index of currently editing row in Step Details table (to prevent add row when editing)
 * @param {string} props.mode - "add" or "edit" mode of the page
 */
const BOMTable = ({
  bomTable = [],
  onBomTableChange,
  editingIndex,
  onEditingIndexChange,
  isHideBomHeading = false,
  onHideBomHeadingChange,
  units = [],
  onAddRow,
  onUnsavedChanges,
  onValidationError,
  onShowWarning,
  editingStepIndex = null,
  mode = "add",
}) => {
  const dispatch = useDispatch();
  const { suggestions } = useSelector((state) => state.suggestion);
  // Parts Modal state
  const [isPartsModalOpen, setPartsModalOpen] = useState(false);
  const [selectedPartIndex, setSelectedPartIndex] = useState(null);

  // Temporary row state - only added to main state when saved
  const [pendingRow, setPendingRow] = useState(null);
  
  // Ref for quantity input to focus after part selection
  const qtyInputRef = useRef(null);
  const [shouldFocusQty, setShouldFocusQty] = useState(false);

  // Store original row data when editing starts (for cancel functionality)
  // Using useRef to ensure we always have the latest original data when cancel is called
  const originalRowDataRef = useRef(null);

  // Fetch unit_type_id_ref when editing a row (only if missing, don't fetch suggestions here)
  useEffect(() => {
    if (editingIndex !== null && editingIndex !== bomTable.length) {
      const row = bomTable[editingIndex];
      if (row && row.partId && !row.unit_type_id_ref) {
        // Only fetch if unit_type_id_ref is missing
        dispatch(getPartById(row.partId))
          .unwrap()
          .then((partData) => {
            const unitTypeIdRef =
              partData?.unit_type_id_ref ||
              partData?.unit_info?.unit_type_id_ref;
            if (unitTypeIdRef) {
              const updated = [...bomTable];
              updated[editingIndex] = {
                ...updated[editingIndex],
                unit_type_id_ref: unitTypeIdRef,
                unit_ref: unitTypeIdRef,
              };
              onBomTableChange(updated);
            }
          })
          .catch((error) => {
            console.error("Error fetching part details:", error);
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingIndex]);

  // Effect to focus quantity input after selecting a part
  useEffect(() => {
    if (shouldFocusQty && qtyInputRef.current) {
      qtyInputRef.current.focus();
      setShouldFocusQty(false);
    }
  }, [shouldFocusQty, pendingRow, bomTable]);

  // Don't fetch suggestion rules here - they're already fetched in AddEditStep.js on mount

  /**
   * Check if a part matches a suggestion rule
   */
  const checkPartMatchesRule = (part, rule) => {
    if (!part || !rule) return false;

    // Check if part type matches rule's applicable types
    const partTypeId = part.type_details?.type_id || part.type_id;
    const ruleTypeIds =
      rule.type_ids || rule.mapped_types?.map((t) => t.type_id) || [];

    if (ruleTypeIds.length > 0 && !ruleTypeIds.includes(partTypeId)) {
      return false;
    }

    // Check if part number matches rule criteria
    const partNumber = part.part_number || "";
    const criteria = rule.criteria || "";
    const criteriaFormat =
      rule.criteria_format_name || rule.criteria_format_ref || "";
    const criteriaFormatLower = criteriaFormat?.toLowerCase() || "";

    if (!criteria) return false;

    try {
      // Check for Regular Expression format
      if (
        criteriaFormat === "Regular Expression" ||
        criteriaFormatLower.includes("regular") ||
        criteriaFormatLower.includes("regex") ||
        criteriaFormatLower.includes("expression")
      ) {
        // Regular expression matching
        const regex = new RegExp(criteria);
        return regex.test(partNumber);
      }
      // Check for Glob format
      else if (
        criteriaFormat === "Glob" ||
        criteriaFormatLower.includes("glob")
      ) {
        // Glob pattern matching (convert glob to regex)
        const globToRegex = (pattern) => {
          return pattern
            .replace(/\./g, "\\.")
            .replace(/\*/g, ".*")
            .replace(/\?/g, ".");
        };
        const regex = new RegExp(`^${globToRegex(criteria)}$`);
        return regex.test(partNumber);
      }
      // Default to Text format (exact equality only)
      else {
        // Text matching (exact equality only)
        return partNumber === criteria;
      }
    } catch (error) {
      console.error("Error matching rule criteria:", error, {
        partNumber,
        criteria,
        criteriaFormat,
      });
      return false;
    }
  };

  /**
   * Get matching suggestions for a part
   * Returns unique suggestion names (deduplicated by rule_name)
   * If multiple rules match with the same name, show only once
   * If rules have different names, show all unique names
   */
  const getMatchingSuggestions = (part) => {
    if (!part || !suggestions || suggestions.length === 0) return [];

    // Get all matching rules
    const matchingRules = suggestions.filter((rule) =>
      checkPartMatchesRule(part, rule)
    );

    if (matchingRules.length === 0) return [];

    // Extract all suggestion names
    const allSuggestions = matchingRules
      .map((rule) => rule.rule_name || rule.suggestion || "")
      .filter(Boolean);
    
    // Deduplicate by name - keep only unique suggestion names while preserving order
    const uniqueSuggestions = allSuggestions.filter(
      (value, index, self) => self.indexOf(value) === index
    );
    
    return uniqueSuggestions;
  };

  // Populate suggestions for all rows in edit mode when data is loaded (only once)
  const suggestionsInitializedRef = useRef(false);
  useEffect(() => {
    if (mode === "edit" && bomTable.length > 0 && suggestions && suggestions.length > 0 && !suggestionsInitializedRef.current) {
      // Check if any row has a partId but no suggestions
      const rowsNeedingSuggestions = bomTable.filter(
        (row) => row.partId && (!row.suggestions || row.suggestions.length === 0)
      );

      if (rowsNeedingSuggestions.length > 0) {
        suggestionsInitializedRef.current = true; // Mark as initialized to prevent re-running
        
        // Fetch part details for rows that need suggestions
        const fetchSuggestionsForRows = async () => {
          const updatedBomTable = [...bomTable];

          for (let i = 0; i < rowsNeedingSuggestions.length; i++) {
            const row = rowsNeedingSuggestions[i];
            const rowIndex = bomTable.findIndex((r) => r.partId === row.partId && (!r.suggestions || r.suggestions.length === 0));

            if (rowIndex !== -1) {
              try {
                const partData = await dispatch(getPartById(row.partId)).unwrap();

                // Construct part object for suggestion matching
                const partForSuggestion = {
                  part_id: partData.part_id,
                  part_number: partData.part_number || row.partNumber,
                  type_details: partData.type_info || row.type_details,
                  type_id: partData.type_info?.type_id || row.type_details?.type_id,
                  description: partData.description || row.description,
                };

                // Get matching suggestions using the helper function
                const matchingSuggestions = getMatchingSuggestions(partForSuggestion);

                if (matchingSuggestions.length > 0) {
                  updatedBomTable[rowIndex] = {
                    ...updatedBomTable[rowIndex],
                    suggestions: matchingSuggestions,
                  };
                }
              } catch (error) {
                console.error(`Error fetching suggestions for part ${row.partId}:`, error);
              }
            }
          }

          // Only update if we found any suggestions
          const hasChanges = updatedBomTable.some(
            (row, index) => row.suggestions && row.suggestions.length > 0 && (!bomTable[index]?.suggestions || bomTable[index].suggestions.length === 0)
          );

          if (hasChanges) {
            onBomTableChange(updatedBomTable);
          }
        };

        fetchSuggestionsForRows();
      } else {
        // No rows need suggestions, mark as initialized
        suggestionsInitializedRef.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, suggestions?.length]);

  // Handle part selection from parts modal
  const handleSelectPart = (selectedPart) => {
    if (selectedPartIndex !== null) {
      // Get matching suggestions for the selected part
      const matchingSuggestions = getMatchingSuggestions(selectedPart);
      // Store as array to display vertically
      const suggestionsArray = matchingSuggestions;

      // If selecting part for pending row
      if (selectedPartIndex === bomTable.length && pendingRow !== null) {
        setPendingRow({
          ...pendingRow,
          unit: selectedPart.units?.[0]?.unit_id || "",
          qty: "",
          partNumber: selectedPart.part_number,
          type: selectedPart.type_details?.name,
          description: selectedPart.description || "",
          partId: selectedPart.part_id,
          unit_type_id_ref: selectedPart.unit_type_id_ref,
          unit_ref: selectedPart.unit_type_id_ref,
          part_ref: selectedPart.part_id,
          type_details: selectedPart.type_details,
          suggestions: suggestionsArray,
          table_note: selectedPart.table_note || "",
        });
      } else {
        // Selecting part for existing row
        const updated = [...bomTable];
        updated[selectedPartIndex] = {
          ...updated[selectedPartIndex],
          unit: selectedPart.units?.[0]?.unit_id || "",
          qty: "",
          partNumber: selectedPart.part_number,
          type: selectedPart.type_details?.name,
          description: selectedPart.description || "",
          table_note: selectedPart.table_note || "",
          partId: selectedPart.part_id,
          unit_type_id_ref: selectedPart.unit_type_id_ref,
          unit_ref: selectedPart.unit_type_id_ref,
          part_ref: selectedPart.part_id,
          type_details: selectedPart.type_details,
          suggestions: suggestionsArray,
        };
        onBomTableChange(updated);
        onUnsavedChanges();
      }
    }
    setPartsModalOpen(false);
    setSelectedPartIndex(null);
    setShouldFocusQty(true);
  };

  const handleEdit = (index) => {
    if (editingIndex !== null || editingStepIndex !== null) {
      onShowWarning(
        "Please save or cancel the current row before adding or editing another one.",
        "error"
      );
      return;
    }
    // If editing pending row, just set editing index
    if (index === bomTable.length && pendingRow !== null) {
      onEditingIndexChange(index);
      return;
    }

    // Editing existing row - clean the table first
    const cleanedBomTable = bomTable.filter((row, i) => {
      return (
        i === index || !(row.isNew && !row.type && !row.partNumber && !row.qty)
      );
    });

    // Store a deep copy of the original row data AFTER filtering but BEFORE any modifications
    if (cleanedBomTable[index]) {
      originalRowDataRef.current = JSON.parse(
        JSON.stringify(cleanedBomTable[index])
      );
    }

    if (cleanedBomTable[index]?.isNew) {
      cleanedBomTable[index].isNew = false;
    }

    onBomTableChange(cleanedBomTable);
    onEditingIndexChange(index);
    onUnsavedChanges();
  };

  const handleSave = (index) => {
    // Check if we're saving a pending row
    const rowToSave =
      index === bomTable.length && pendingRow !== null
        ? pendingRow
        : bomTable[index];

    // Validation: Item Code (partNumber) is required
    if (!rowToSave.partNumber || rowToSave.partNumber.trim() === "") {
      if (onValidationError) {
        onValidationError("Please select an Item Code before saving.");
      }
      return;
    }

    // Validation: If unit is selected, quantity is required
    if (rowToSave.unit && !rowToSave.qty) {
      if (onValidationError) {
        onValidationError("Please enter quantity when unit is selected.");
      }
      return;
    }

    // If saving a pending row, add it to the main state
    if (index === bomTable.length && pendingRow !== null) {
      const newRow = {
        ...pendingRow,
        isNew: false, // Mark as saved
      };

      // Clean up any blank rows from main state
      const cleanedBomTable = bomTable.filter((row) => {
        return !(row.isNew && !row.type && !row.partNumber && !row.qty);
      });

      // Add the new saved row
      onBomTableChange([...cleanedBomTable, newRow]);
      setPendingRow(null);
    } else {
      // Saving an existing row
      const cleanedBomTable = bomTable.filter((row, i) => {
        return (
          i === index ||
          !(row.isNew && !row.type && !row.partNumber && !row.qty)
        );
      });

      // Mark as saved
      if (cleanedBomTable[index]) {
        cleanedBomTable[index].isNew = false;
      }

      onBomTableChange(cleanedBomTable);
    }

    onEditingIndexChange(null);
    originalRowDataRef.current = null;
    onUnsavedChanges();
  };

  const handleCancel = (index) => {
    // If canceling a pending row, just clear it
    if (index === bomTable.length && pendingRow !== null) {
      setPendingRow(null);
      onEditingIndexChange(null);
      originalRowDataRef.current = null;
      return;
    }

    // Cancel existing row - restore original data
    if (bomTable[index]?.isNew) {
      const updated = [...bomTable];
      updated.splice(index, 1);
      onBomTableChange(updated);
      onEditingIndexChange(null);
      originalRowDataRef.current = null;
    } else {
      // Restore the original row data
      if (
        originalRowDataRef.current !== null &&
        bomTable[index] !== undefined
      ) {
        const updated = [...bomTable];
        updated[index] = { ...originalRowDataRef.current };
        onBomTableChange(updated);
      }
      onEditingIndexChange(null);
      originalRowDataRef.current = null;
    }
    onUnsavedChanges();
  };

  const handleDelete = (index) => {
    if (editingIndex !== null || editingStepIndex !== null) {
      onShowWarning(
        "Please save or cancel the current row before adding or editing another one.",
        "error"
      );
      return;
    }
    // Can't delete pending row (just cancel it)
    if (index === bomTable.length && pendingRow !== null) {
      setPendingRow(null);
      onEditingIndexChange(null);
      return;
    }

    // Delete existing row
    const cleanedBomTable = bomTable.filter((row, i) => {
      return (
        i !== index && !(row.isNew && !row.type && !row.partNumber && !row.qty)
      );
    });
    onUnsavedChanges();
    onBomTableChange(cleanedBomTable);
  };

  const handleAddRow = () => {
    // Check if an existing BOM row is being edited (not a pending row)
    if (editingIndex !== null && editingIndex !== bomTable.length) {
      if (onShowWarning) {
        onShowWarning(
          "Please save or cancel the current row before adding or editing another one.",
          "error"
        );
      }
      return;
    }

    // Check if Step Details table is editing a row (only check if BOM table is not editing)
    if (editingStepIndex !== null) {
      if (onShowWarning) {
        onShowWarning(
          "Please save or cancel the Step Details row before adding a new BOM row.",
          "error"
        );
      }
      return;
    }

    // If there's already a pending row, just focus on it
    if (pendingRow !== null) {
      if (onShowWarning) {
        onShowWarning(
          "Please save or cancel the current row before adding or editing another one.",
          "error"
        );
      }
      onEditingIndexChange(bomTable.length); // Edit the pending row
      return;
    }

    // Check if there's already a blank row in the main state
    const hasBlankRow = bomTable.some(
      (row) => row.isNew && !row.type && !row.partNumber && !row.qty
    );

    if (hasBlankRow) {
      if (onShowWarning) {
        onShowWarning(
          "Please save or cancel the current row before adding or editing another one.",
          "error"
        );
      }
      const blankRowIndex = bomTable.findIndex(
        (row) => row.isNew && !row.type && !row.partNumber && !row.qty
      );
      onEditingIndexChange(blankRowIndex);
      return;
    }

    // Create a new pending row (not added to state yet)
    const newRow = {
      type: "",
      partNumber: "",
      description: "",
      qty: "",
      unit: "",
      suggestion: "",
      table_note: "",
      isNew: true,
    };
    setPendingRow(newRow);
    onEditingIndexChange(bomTable.length); // Set editing index to the "new" row position
  };

  const updateBomRow = (index, updates) => {
    // If updating pending row, update pending state
    if (index === bomTable.length && pendingRow !== null) {
      setPendingRow({ ...pendingRow, ...updates });
      return;
    }

    // Update existing row
    const updated = [...bomTable];
    updated[index] = { ...updated[index], ...updates };
    onBomTableChange(updated);
    onUnsavedChanges();
  };

  const clearPartNumber = (index) => {
    updateBomRow(index, {
      unit: "",
      partNumber: "",
      type: "",
      description: "",
      partId: "",
      unit_type_id_ref: "",
      suggestions: [],
      suggestion: "",
      table_note: "",
    });
  };

  const renderCell = (row, field) => {
    if (field === "partNumber") {
      return <p>{row[field]}</p>;
    }
    if (field === "description") {
      return (
        <>
          <RenderHtml content={row.description} />
          {row.table_note && (
            <div style={{ marginTop: "4px" }}>
              <RenderHtml content={row.table_note} />
            </div>
          )}
        </>
      );
    }
    if (field === "qty") {
      return row[field] === 0 ? "" : row[field];
    }
    if (field === "unit") {
      let unitName = getUnitNameById(row.unit, units);
      return unitName;
    }
    if (field === "suggestion") {
      const suggestions =
        row.suggestions || (row.suggestion ? [row.suggestion] : []);
      return (
        <div
          style={{
            padding: "4px 8px",
            fontSize: "13px",
            color: "#333",
          }}
        >
          {suggestions.length > 0 &&
            suggestions.map((suggestion, idx) => (
              <div
                key={idx}
                style={{
                  marginBottom: idx < suggestions.length - 1 ? "4px" : "0",
                }}
              >
                {suggestions.length > 1
                  ? `${idx + 1}. ${suggestion}`
                  : suggestion}
              </div>
            ))}
        </div>
      );
    }
    return row[field];
  };

  const renderEditCell = (row, field, index) => {
    if (field === "type") {
      return (
        <TableInput
          type="text"
          value={row.type}
          disabled
          onChange={(e) => updateBomRow(index, { type: e.target.value })}
        />
      );
    }

    if (field === "partNumber") {
      return (
        <InputWithPen
          val={row.partNumber}
          onClickOnPencil={() => {
            setSelectedPartIndex(index);
            setPartsModalOpen(true);
            onUnsavedChanges();
          }}
          onClear={() => clearPartNumber(index)}
        />
      );
    }

    if (field === "description") {
      return (
        <>
          {/* <RenderHtml
            content={row.description}
            inptDec={true}
            customeHeight="26px"
          /> */}
          <InputDescription content={`${row.description} <div style="margin-top: 2px;margin-bottom:2px">${row.table_note}</div>`} />
        </>
      );
    }

    if (field === "qty") {
      return (
        <TableInput
          type="number"
          min="0"
          value={parseFloat(row.qty) === 0 ? "" : row.qty}
          onChange={(e) => {
            const value = Math.max(0, parseFloat(e.target.value) || 0);
            updateBomRow(index, { qty: value });
          }}
          disabled={!row.unit}
          ref={qtyInputRef}
        />
      );
    }

    if (field === "unit") {
      let unitName = getUnitNameById(row.unit, units);
      return (
        <RenderUnits
          key={`${index}-${row.unit_type_id_ref || "no-unit-type"}`}
          units={units || []}
          unit_type_id={row.unit_type_id_ref}
          defaultValue={
            row.unit ? { value: row.unit, label: unitName } : undefined
          }
          onChange={(d) => {
            updateBomRow(index, {
              unit: d?.value || "",
              qty: "",
            });
          }}
        />
      );
    }

    if (field === "suggestion") {
      const suggestions =
        row.suggestions || (row.suggestion ? [row.suggestion] : []);
      
      // Deduplicate suggestions - keep only unique values
      const uniqueSuggestions = suggestions.filter(
        (value, index, self) => self.indexOf(value) === index
      );
      
      return (
        <div
          style={{
            padding: "4px 8px",
            fontSize: "13px",
            color: "#333",
            minHeight: "25px",
          }}
        >
          {uniqueSuggestions.length > 0 &&
            uniqueSuggestions.map((suggestion, idx) => (
              <div
                key={idx}
                style={{
                  marginBottom: idx < uniqueSuggestions.length - 1 ? "4px" : "0",
                }}
              >
                {uniqueSuggestions.length > 1
                  ? `${idx + 1}. ${suggestion}`
                  : suggestion}
              </div>
            ))}
        </div>
      );
    }

    return row[field];
  };

  return (
    <>
      <div className="bom-table-header">
        <p className="bom-table-title">BOM Table for Step</p>
        {/* <CheckboxField
          label="Hide BOM Table from Document"
          checked={isHideBomHeading}
          onChange={(e) => {
            onHideBomHeadingChange(e.target.checked);
            onUnsavedChanges();
          }}
          labelStyle={{
            fontSize: "15px",
            fontWeight: "normal",
            marginBottom: "-3px",
          }}
        /> */}
      </div>

      <EditableTable
        className="table"
        style={{ marginTop: "-10px" }}
        columns={[
          { label: "Type", field: "type", width: "15%", align: "left" },
          { label: "Item Code", field: "partNumber", width: "15%" },
          { label: "Description", field: "description", width: "20%" },
          { label: "Qty", field: "qty", width: "10%" },
          { label: "Unit", field: "unit", width: "10%" },
          { label: "Suggestion", field: "suggestion", width: "20%" },
          { label: "Action", type: "actions", width: "10%" },
        ]}
        data={useMemo(() => {
          // Include pending row in data for display/editing
          return pendingRow !== null && editingIndex === bomTable.length
            ? [...bomTable, pendingRow]
            : bomTable;
        }, [bomTable, pendingRow, editingIndex])}
        editingIndex={editingIndex}
        onEdit={handleEdit}
        onSave={handleSave}
        onCancel={handleCancel}
        onDelete={handleDelete}
        renderCell={renderCell}
        renderEditCell={renderEditCell}
      />

      <AddRowBtn
        onClick={handleAddRow}
        style={{ marginTop: "10px", marginLeft: "-15px" }}
      />
      <div className="step-spacing-bottom"></div>

      {/* Parts Modal */}
      {isPartsModalOpen && (
        <PartsPopup
          handleSelectPart={handleSelectPart}
          setPartsModalOpen={setPartsModalOpen}
        />
      )}
    </>
  );
};

export default BOMTable;
